import * as ko from "knockout";


/**
 * An observable that tracks the dirty status of a specified root object.
 */
export interface KnockoutDirtyFlag extends ko.Observable<boolean> {

    /**
     * Retrieves the current object value as a JSON object.
     */
    currentJSON(): string;

    /**
     * Returns whether the dirty flag is in the 'suspended' state.
     */
    isSuspended(): boolean;

    /**
     * Resets the state of the dirty flag tracker.
     */
    reset(): void;

    /**
     * Resumes a suspended dirty flag tracker.
     * @param resetOnResume
     *     If true, will reset the state of the tracker upon resuming.
     */
    resume(resetOnResume?: boolean): void;

    /**
     * Suspends a dirty flag tracker, such that object changes will not change the dirty state.
     */
    suspend(): void;

    /**
     * The original value of the root object.
     * Only available for non-persistent dirty flags.
     */
    originalJSON?: ko.Observable<string>;

}


/**
 * Persistent dirty flag states.
 */
enum DirtyState {
    Unknown = 0,
    Clean = 1,
    Suspended = 2,
    Dirty = 3
};


/**
 * Attaches a Knockout binding handler to "ko.bindingHandlers".
 *
 * This function is safe to call multiple times. If it is called twice for the
 * same binding, it will verify that the binding is as given. If so, it will
 * continue and return the binding. If not, it will throw an error.
 *
 * @param name
 *    Name of the binding handler.
 * @param handler
 *    Handler that defines "init" and/or "update" functions.
 * @returns
 *    Handler instance.
 * @throws
 *     If the specified binding name has already been assigned
 *     to a different binding handler.
 */
export function addBindingHandler<THandler extends ko.BindingHandler>(name: string, handler: THandler): THandler {
    if (typeof name !== "string" || name.length === 0) {
        throw new Error("A name must be specified for the binding handler");
    }

    if (!ko.bindingHandlers[name]) {
        ko.bindingHandlers[name] = handler;
    } else if (ko.bindingHandlers[name] !== handler) {
        throw new Error(`"ko.bindingHandlers.${name}" has been hijacked, unable to activate`);
    }
    return handler;
}


/**
 * Creates a KnockoutDirtyFlag that recalculates the 'dirty' state whenever a contained value changes.
 *
 * Inspired by http://www.knockmeout.net/2011/05/creating-smart-dirty-flag-in-knockoutjs.html
 *
 * @param root
 *     The root object to track.
 * @param isSuspended
 *     Whether the dirty tracker starts in the 'suspended' state.
 * @return
 *     An KnockoutDirtyFlag observable indicating whether the root object is dirty.
 */
function createTrackingDirtyFlag(root: any, isSuspended: boolean): KnockoutDirtyFlag {
    let originalJSON: ko.Observable<any>;
    let resumeResetRequested = false;
    const isSuspendedObservable = ko.observable(!!isSuspended);

    // subscribe to all observables in root (recursively),
    // which causes this dependent observable to re-evaluate
    // every time any observable in "root" changes
    const currentJSON = ko.computed(function (): any {
        if (isSuspendedObservable()) {
            return null; // while dirtyFlag is suspended, only subscribe to isSuspendedObservable changes
        }
        const rootJson = ko.toJSON(root); // subscribe to every observable in the model
        if (resumeResetRequested) {
            resumeResetRequested = false;
            originalJSON(rootJson); // when this computation function finishes, dirtyTest function will run (subcribed to currentJSON); it will set isDirty to false (because both original and current JSON now point to the same value)
        }
        return rootJson;
    });

    // take snapshot of "root" at creation time
    // (can be updated with "reset" function, see below)
    originalJSON = ko.observable(currentJSON());

    // initialize isDirty observable
    const isDirty = <KnockoutDirtyFlag>ko.observable(false);
    isDirty.originalJSON = originalJSON;
    isDirty.currentJSON = currentJSON;
    isDirty.reset = () => {
        isDirty.resume(true);
    };
    isDirty.suspend = () => {
        isSuspendedObservable(true);
    };
    isDirty.resume = (resetOnResume) => {
        if (isSuspendedObservable()) {
            resumeResetRequested = resetOnResume;
            isSuspendedObservable(false);
        } else if (resetOnResume) {
            originalJSON(currentJSON());
            isDirty(false);
        }
    };
    isDirty.isSuspended = () => {
        return isSuspendedObservable.peek();
    };

    // recompute dirty flag each time currentJSON changes
    currentJSON.subscribe(function dirtyTest(): void {
        if (!isSuspendedObservable()) { // only update isDirty if not suspended
            isDirty(currentJSON() !== originalJSON());
        }
    });

    return isDirty;
}


/**
 * Creates a KnockoutDirtyFlag that stops tracking the 'dirty' state after the first change.
 * Even if the change is subsequently reverted, the 'dirty' state persists.
 *
 * @param root
 *     The root object to track.
 * @param isSuspended
 *     Whether the dirty tracker starts in the 'suspended' state.
 * @return
 *     An KnockoutDirtyFlag observable indicating whether the root object is dirty.
 */
function createPersistentDirtyFlag(root: any, isSuspended: boolean): KnockoutDirtyFlag {
    const isDirty = <KnockoutDirtyFlag>ko.observable(false);
    const stateObservable = ko.observable(isSuspended ? DirtyState.Suspended : DirtyState.Unknown);

    ko.computed(function persistentDirtyFlagChangeMonitor(): void {
        switch (stateObservable()) { // always subscribe to "stateObservable"
        case DirtyState.Unknown:
            ko.toJS(root); // subscribe to every observable in the model
            stateObservable(DirtyState.Clean);
            isDirty(false);
            break;
        case DirtyState.Clean: // not dirty, observing all -- we only get notified in this state if a model observable "wakes" us up
            stateObservable(DirtyState.Dirty); // mark as dirty
            isDirty(true);
            break;
        default: // suspended OR already dirty, couldn't care less :)
            break;
        }
    });

    isDirty.currentJSON = () => ko.toJSON(root); // maintain interface of tracking dirty flag, as expected by InitViewModel
    isDirty.suspend = () => {
        stateObservable(DirtyState.Suspended);
    };
    isDirty.resume = (resetOnResume) => {
        if (stateObservable.peek() === DirtyState.Suspended || resetOnResume) { // only resume when suspended or when forced
            stateObservable(DirtyState.Unknown);
        }
    };
    isDirty.reset = () => {
        if (stateObservable.peek() !== DirtyState.Clean) { // only reset if not already observing model
            stateObservable(DirtyState.Unknown);
        }
    };
    isDirty.isSuspended = () => stateObservable.peek() === DirtyState.Suspended;

    return isDirty;
}


/**
 * Creates a KnockoutDirtyFlag instance.
 * @param root
 *     Root object used to track changes.
 * @param isSuspended
 *     If true, starts in a 'suspended' state (does not track changes until 'resume' called).
 * @param isPersistent
 *     If true, 'dirty' state persists after the first change is detected and until 'reset' is called.
 */
export function dirtyFlag(root: any, isSuspended: boolean = false, isPersistent: boolean = false): KnockoutDirtyFlag {
    return isPersistent
        ? createPersistentDirtyFlag(root, isSuspended)
        : createTrackingDirtyFlag(root, isSuspended);
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/knockout/util.ts